import { useEffect, useRef } from 'react';
import { ChatMessage } from '@/types/chatbot.types';

interface UseChatScrollReturn {
  messagesEndRef: React.RefObject<HTMLDivElement>;
  messagesContainerRef: React.RefObject<HTMLDivElement>;
}

/**
 * Custom hook to handle auto-scrolling in chat messages
 * - Scrolls to bottom when new messages are added
 * - Tracks user scrolling to prevent interference
 * - Handles real-time scrolling during message streaming
 */
export const useChatScroll = (messages: ChatMessage[]): UseChatScrollReturn => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const isUserScrollingRef = useRef<boolean>(false);
  const scrollCheckTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Auto-scroll to bottom when messages change
  const scrollToBottom = (smooth = true) => {
    // Don't auto-scroll if user is manually scrolling
    if (isUserScrollingRef.current) {
      return;
    }

    // Use requestAnimationFrame for smooth scrolling
    requestAnimationFrame(() => {
      if (isUserScrollingRef.current) {
        return;
      }

      // Always use container scroll method to avoid page-level scrolling issues
      // This is especially important with scaled containers
      if (messagesContainerRef.current) {
        const container = messagesContainerRef.current;
        if (smooth) {
          container.scrollTo({
            top: container.scrollHeight,
            behavior: 'smooth',
          });
        } else {
          container.scrollTop = container.scrollHeight;
        }
      } else if (messagesEndRef.current) {
        // Fallback: use scrollIntoView with constraints to prevent page scrolling
        messagesEndRef.current.scrollIntoView({
          behavior: smooth ? 'smooth' : 'auto',
          block: 'nearest',
          inline: 'nearest',
        });
      }
    });
  };

  // Scroll when messages array changes (new message added)
  useEffect(() => {
    scrollToBottom(true);
  }, [messages.length]);

  // Track user scrolling to prevent auto-scroll interference
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    let lastScrollTop = container.scrollTop;

    const handleScroll = () => {
      const currentScrollTop = container.scrollTop;
      const scrollHeight = container.scrollHeight;
      const clientHeight = container.clientHeight;
      const isNearBottom = scrollHeight - currentScrollTop - clientHeight < 100;

      // If user scrolls up, mark as user scrolling
      if (currentScrollTop < lastScrollTop || !isNearBottom) {
        isUserScrollingRef.current = true;

        // Clear any existing timeout
        if (scrollCheckTimeoutRef.current) {
          clearTimeout(scrollCheckTimeoutRef.current);
        }

        // Reset user scrolling flag after user stops scrolling for 1 second
        scrollCheckTimeoutRef.current = setTimeout(() => {
          isUserScrollingRef.current = false;
        }, 1000);
      } else if (isNearBottom) {
        // If user scrolls back to bottom, allow auto-scroll again
        isUserScrollingRef.current = false;
      }

      lastScrollTop = currentScrollTop;
    };

    container.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      container.removeEventListener('scroll', handleScroll);
      if (scrollCheckTimeoutRef.current) {
        clearTimeout(scrollCheckTimeoutRef.current);
      }
    };
  }, []);

  // Scroll during streaming (content/widgets updating)
  // Use MutationObserver to watch for DOM changes during streaming for real-time scrolling
  useEffect(() => {
    const lastMessage = messages[messages.length - 1];
    const isStreaming = lastMessage?.isStreaming || false;

    if (!isStreaming) {
      return;
    }

    let rafId: number | null = null;
    let lastScrollHeight = 0;

    // Throttled scroll function using requestAnimationFrame
    const throttledScroll = () => {
      if (rafId !== null) {
        return;
      }

      rafId = requestAnimationFrame(() => {
        if (isUserScrollingRef.current) {
          rafId = null;
          return;
        }

        if (messagesContainerRef.current) {
          const currentScrollHeight = messagesContainerRef.current.scrollHeight;
          // Only scroll if content actually changed
          if (currentScrollHeight !== lastScrollHeight) {
            messagesContainerRef.current.scrollTop = currentScrollHeight;
            lastScrollHeight = currentScrollHeight;
          }
        } else if (messagesEndRef.current) {
          // Fallback: use scrollIntoView with constraints to prevent page scrolling
          messagesEndRef.current.scrollIntoView({
            behavior: 'auto',
            block: 'nearest',
            inline: 'nearest',
          });
        }
        rafId = null;
      });
    };

    // During streaming, use MutationObserver to watch for DOM changes
    const observer = new MutationObserver(throttledScroll);

    // Observe the messages container for child changes
    if (messagesContainerRef.current) {
      observer.observe(messagesContainerRef.current, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    }

    // Throttled interval as a fallback (less frequent)
    const scrollInterval = setInterval(throttledScroll, 200);

    return () => {
      observer.disconnect();
      clearInterval(scrollInterval);
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [messages.length, messages[messages.length - 1]?.isStreaming]);

  return {
    messagesEndRef,
    messagesContainerRef,
  };
};
